import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Stack,
  TextField,
  Typography,
  CircularProgress,
  Paper,
  Avatar,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { Formik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { ENDPOINT, IMAGE_BASE_URL } from "../../../endpoints/endpoints";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const UserUpdate = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [initialValues, setInitialValues] = useState({
    username: "",
    email: "",
    phone: "",
    profile_image: null,
    groups: [],
  });

  const [preview, setPreview] = useState(null);
  const [groupOptions, setGroupOptions] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  // ✅ Helper to convert full URL
  const getProfileImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith("http")) return imagePath;
    return `${IMAGE_BASE_URL}${imagePath}`;
  };

  // ✅ Validation rules
  const validationSchema = Yup.object({
    username: Yup.string().required("Username is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    phone: Yup.string()
      .matches(/^\+?95[0-9]{7,11}$/, "Enter valid Myanmar phone (+959...)")
      .required("Phone number is required"),
  });

  // ✅ Token check
  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (!token) navigate("/login", { replace: true });
  }, [navigate]);

  // ✅ Recursive fetch all group pages
  const fetchAllGroups = async (url, token, allGroups = []) => {
    const res = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const names = res.data.results.map((g) => g.name);
    const combined = [...allGroups, ...names];
    if (res.data.next) {
      return fetchAllGroups(res.data.next, token, combined);
    }
    return combined;
  };

  const fetchGroups = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const allGroups = await fetchAllGroups(ENDPOINT.GROUP_LIST, token);
      setGroupOptions(allGroups);
      setFilteredGroups(allGroups);
    } catch (error) {
      console.error("Error fetching groups:", error);
    }
  };

  // ✅ Fetch user detail + groups
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        if (!token) {
          navigate("/login", { replace: true });
          return;
        }

        const [userRes] = await Promise.all([
          axios.get(ENDPOINT.USER_DETAIL(id), {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetchGroups(),
        ]);

        if (userRes.data.success) {
          const user = userRes.data.user;
          setInitialValues({
            username: user.username,
            email: user.email,
            phone: user.phone,
            profile_image: user.profile_image,
            groups:
              user.groups?.map((g) =>
                typeof g === "object" ? g.name : g
              ) || [],
          });
          setPreview(getProfileImageUrl(user.profile_image));
        } else {
          toast.error("Failed to fetch user data");
        }
      } catch (err) {
        console.error("Error fetching user:", err);
        toast.error("Failed to load user detail");
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [id, navigate]);

  // ✅ Handle image preview
  const handleImageChange = (e, setFieldValue) => {
    const file = e.target.files[0];
    if (file) {
      setFieldValue("profile_image", file);
      setPreview(URL.createObjectURL(file));
    }
  };

  // ✅ Submit updated data
  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem("accessToken");
      const formData = new FormData();
      formData.append("username", values.username);
      formData.append("email", values.email);
      formData.append("phone", values.phone);
      values.groups.forEach((g) => formData.append("groups", g));
      if (values.profile_image instanceof File) {
        formData.append("profile_image", values.profile_image);
      }

      const res = await axios.put(ENDPOINT.USER_UPDATE(id), formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      if (res.data.success) {
        toast.success(res.data.message || "User updated successfully");
        setTimeout(() => {
          navigate("/users/list", {
            state: { toastMessage: "User updated successfully" },
          });
        }, 1200);
      } else {
        toast.error(res.data.message || "Failed to update user");
      }
    } catch (err) {
      console.error("Error updating user:", err);
      if (err.response?.data?.errors) {
        const msgs = Object.values(err.response.data.errors).flat().join(", ");
        toast.error(msgs);
      } else {
        toast.error("Something went wrong");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ✅ UI render
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 6 }}>
      <ToastContainer position="top-right" autoClose={3000} />
      <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2} mb={3}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/users/list")}
          >
            Back
          </Button>
          <Typography variant="h5" fontWeight={600}>
            ✏️ Update User
          </Typography>
        </Stack>

        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
          }) => (
            <form onSubmit={handleSubmit} noValidate>
              <Stack spacing={3}>
                {/* Profile Image */}
                <Stack direction="row" alignItems="center" spacing={3}>
                  <Avatar
                    sx={{ width: 80, height: 80, bgcolor: "primary.main" }}
                    src={preview}
                  >
                    {!preview && values.username?.charAt(0).toUpperCase()}
                  </Avatar>
                  <Button variant="outlined" component="label">
                    Upload New Image
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(e) => handleImageChange(e, setFieldValue)}
                    />
                  </Button>
                </Stack>

                {/* Username */}
                <TextField
                  label="Username"
                  name="username"
                  value={values.username}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.username && Boolean(errors.username)}
                  helperText={touched.username && errors.username}
                  fullWidth
                />

                {/* Email */}
                <TextField
                  label="Email"
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                  fullWidth
                />

                {/* Phone */}
                <TextField
                  label="Phone"
                  name="phone"
                  value={values.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.phone && Boolean(errors.phone)}
                  helperText={touched.phone && errors.phone}
                  fullWidth
                  placeholder="+959..."
                />

                {/* ✅ Groups with Manual Search + Switch UI */}
                <Box sx={{ mt: 2 }}>
                  <Typography fontWeight={600} mb={1}>
                    Groups
                  </Typography>

                  {/* 🔍 Search */}
                  <TextField
                    size="small"
                    placeholder="Search group name and press Enter..."
                    fullWidth
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const val = searchQuery.toLowerCase().trim();
                        if (val === "") {
                          setFilteredGroups(groupOptions);
                        } else {
                          const filtered = groupOptions.filter((g) =>
                            g.toLowerCase().includes(val)
                          );
                          setFilteredGroups(filtered);
                        }
                      }
                    }}
                    sx={{ mb: 2 }}
                  />

                  {/* 🟢 Switch List */}
                  {filteredGroups.length > 0 ? (
                    <Box
                      sx={{
                        border: "1px solid #ddd",
                        borderRadius: 2,
                        p: 2,
                        maxHeight: 250,
                        overflowY: "auto",
                      }}
                    >
                      {filteredGroups.map((group) => (
                        <Stack
                          key={group}
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{
                            borderBottom: "1px solid #f0f0f0",
                            py: 0.5,
                            "&:last-child": { borderBottom: "none" },
                          }}
                        >
                          <Typography>{group}</Typography>
                          <Button
                            variant={
                              values.groups.includes(group)
                                ? "contained"
                                : "outlined"
                            }
                            color={
                              values.groups.includes(group)
                                ? "primary"
                                : "inherit"
                            }
                            size="small"
                            sx={{
                              textTransform: "none",
                              minWidth: 80,
                            }}
                            onClick={() => {
                              if (values.groups.includes(group)) {
                                setFieldValue(
                                  "groups",
                                  values.groups.filter((g) => g !== group)
                                );
                              } else {
                                setFieldValue("groups", [
                                  ...values.groups,
                                  group,
                                ]);
                              }
                            }}
                          >
                            {values.groups.includes(group) ? "ON" : "OFF"}
                          </Button>
                        </Stack>
                      ))}
                    </Box>
                  ) : (
                    <Typography color="text.secondary" fontSize={14}>
                      No groups found.
                    </Typography>
                  )}
                </Box>

                {/* Buttons */}
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => navigate("/users/list")}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <CircularProgress size={20} sx={{ mr: 1 }} /> Updating...
                      </>
                    ) : (
                      "Update User"
                    )}
                  </Button>
                </Stack>
              </Stack>
            </form>
          )}
        </Formik>
      </Paper>
    </Container>
  );
};

export default UserUpdate;
